import { ROUTES } from '@/constants'
import { usePatientProfile } from '@/services/patients'
import { setSidebarMobileOpen, useAppDispatch } from '@/store'
import {
  CalendarMonth as CalendarIcon,
  CreditCard,
  Dashboard as DashboardIcon,
  Article as DocumentsIcon,
  Science as LabIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Medication as PillIcon,
  Assignment as PlanIcon,
  Settings as SettingsIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material'
import { AppBar, Box, IconButton, Toolbar, useMediaQuery, useTheme } from '@mui/material'
import { Outlet } from 'react-router-dom'
import type { SidebarNavItem } from './Sidebar'
import { Sidebar } from './Sidebar'
import { useBrand } from '@/branding'

const PATIENT_NAV_ITEMS: SidebarNavItem[] = [
  { to: ROUTES.PATIENT.DASHBOARD, icon: <DashboardIcon />, label: 'Dashboard', end: true },
  { to: ROUTES.PATIENT.APPOINTMENTS, icon: <CalendarIcon />, label: 'Appointments' },
  { to: ROUTES.PATIENT.DOCUMENTS, icon: <DocumentsIcon />, label: 'Documents' },
  { to: ROUTES.PATIENT.SUBSCRIPTIONS, icon: <CreditCard />, label: 'Subscriptions' },
  { to: ROUTES.PATIENT.PLAN_OF_CARE, icon: <PlanIcon />, label: 'Plan of Care' },
  { to: ROUTES.PATIENT.LAB_REPORTS, icon: <LabIcon />, label: 'Lab Reports' },
  { to: ROUTES.PATIENT.SUPPLEMENTS, icon: <PillIcon />, label: 'Supplements' },
  { to: ROUTES.PATIENT.PURCHASES, icon: <ShoppingBagIcon />, label: 'Purchases' },
  // { to: ROUTES.PATIENT.SUPERBILLS, icon: <FileIcon />, label: 'Superbills' },
  { to: ROUTES.PATIENT.PAYMENT_METHODS, icon: <CreditCard />, label: 'Payment Methods' },
]

const PATIENT_FOOTER_ITEMS: SidebarNavItem[] = [
  { to: ROUTES.PATIENT.PROFILE, icon: <PersonIcon />, label: 'Profile' },
  { to: ROUTES.PATIENT.SETTINGS, icon: <SettingsIcon />, label: 'Settings' },
]

/**
 * PatientPortalLayout — patient-facing app shell.
 * Shares the same Sidebar component with the clinician portal for visual
 * consistency (collapsible, tooltips, theme toggle, etc).
 */
export const PatientPortalLayout = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dispatch = useAppDispatch()
  const brand = useBrand()
  const { data: patient } = usePatientProfile()

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}
    >
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={() => dispatch(setSidebarMobileOpen(true))}>
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={brand.logo}
              alt={brand.name}
              sx={{ height: 28, width: 'auto', ml: 1 }}
            />
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar — logout falls back to the shared /auth/login; portal is
          auto-detected from hostname / VITE_PORTAL, not from the URL path. */}
      <Sidebar
        navItems={PATIENT_NAV_ITEMS}
        footerItems={PATIENT_FOOTER_ITEMS}
        profileItemTo={ROUTES.PATIENT.PROFILE}
        avatarUrl={patient?.avatar}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          pt: isMobile ? 8 : 3,
          pb: 3,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}
